import Vue from 'vue';
import store from '../store';

const DEFAULT_DURATION = 1000;
const timerMap = new WeakMap();

/**
 * 按钮权限 + 防重复点击指令
 * @description v-permit:disabled="'btn.add'"            无权限禁用
 * @description v-permit:remove="'btn.add'"              无权限移除
 * @description v-permit:remove="{ permit: 'btn.add', duration: 3000 }"  自定义时长
 * @description v-permit:remove="{ permit: 'btn.add', duration: 0 }"     永久禁用
 */
const permit = Vue.directive('permit', {
  inserted(el, binding) {
    const buttonPermissions = store.getters.button_permissions;

    // 解析参数，兼容字符串和对象两种写法
    const isObject = typeof binding.value === 'object';
    const permitKey = isObject ? binding.value.permit : binding.value;
    const duration = isObject ? (binding.value.duration ?? DEFAULT_DURATION) : DEFAULT_DURATION;

    const hasPermit = buttonPermissions.find((item) => item === permitKey);

    // ---- 鉴权 ----
    if (binding.arg === 'disabled' && !hasPermit) {
      el.disabled = 'disabled';
      el.classList.add('is-disabled');
      return;
    } else if (binding.arg === 'remove' && !hasPermit) {
      el.parentNode && el.parentNode.removeChild(el);
      return;
    }

    // ---- 防重复点击（有权限才注册） ----
    const storageKey = 'click_once_' + permitKey;

    const setDisabled = () => {
      el.setAttribute('disabled', 'disabled');
      el.classList.add('is-disabled');
      el.disabled = true;
    };

    const setEnabled = () => {
      el.removeAttribute('disabled');
      el.classList.remove('is-disabled');
      el.disabled = false;
      localStorage.removeItem(storageKey);
    };

    const scheduleRecover = (timestamp) => {
      if (duration === 0) return;

      const remaining = duration - (Date.now() - timestamp);
      if (remaining <= 0) {
        setEnabled();
        return;
      }
      const timer = setTimeout(() => {
        setEnabled();
        timerMap.delete(el);
      }, remaining);
      timerMap.set(el, timer);
    };

    // 挂载时检查，刷新后恢复禁用状态
    const record = localStorage.getItem(storageKey);
    if (record) {
      const { timestamp } = JSON.parse(record);
      const isStillDisabled = duration === 0 || Date.now() - timestamp < duration;
      if (isStillDisabled) {
        setDisabled();
        scheduleRecover(timestamp);
      } else {
        localStorage.removeItem(storageKey);
      }
    }

    el._originalDisabled = el.disabled;

    const handler = (event) => {
      if (timerMap.has(el)) {
        event.stopImmediatePropagation();
        event.preventDefault();
        return;
      }
      const timestamp = Date.now();
      localStorage.setItem(storageKey, JSON.stringify({ timestamp }));
      setDisabled();
      scheduleRecover(timestamp);
    };

    el._permitHandler = handler;
    el.addEventListener('click', handler, true);
  },

  unbind(el) {
    if (el._permitHandler) {
      el.removeEventListener('click', el._permitHandler, true);
      delete el._permitHandler;
    }
    const timer = timerMap.get(el);
    if (timer) {
      clearTimeout(timer);
      timerMap.delete(el);
    }
  }
});

export { permit };
